package com.iregained.site.components.widgets

import androidx.compose.runtime.*
import com.iregained.site.components.widgets.styles.RegionSwitcherBannerStyle
import com.iregained.site.components.widgets.styles.RegionSwitcherButtonStyle
import com.iregained.site.components.widgets.styles.RegionSwitcherCloseIconStyle
import com.iregained.site.region.Region
import com.iregained.site.region.RegionConfig
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.localStorage
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

// Key for localStorage persistence
private const val BANNER_DISMISSED_KEY = "site:regionBannerDismissed"

// CMS DATA MODEL & MEDIA SPECS
// - Aspect Ratio: 1:1 (Square) for flags.
// - Recommended Dimensions: 32x32px or 64x64px.
// - Format: SVG or PNG with transparent background.
data class RegionSwitcherBannerData(
    val suggestedRegion: Region,
    val flagIconPath: String,
    val messageText: String, // e.g., "Visit our dedicated site for:"
    val ctaText: String,    // e.g., "Go to .in site"
    val ctaUrl: String      // e.g., "https://iregained.in"
)

// Define default data based on RegionConfig
// FIXED: The goal is to suggest the global site (.rehab) when on region-specific sites (.in, .us).
private val defaultRegionBannerData = when (RegionConfig.currentRegion) {
    Region.INDIA -> RegionSwitcherBannerData(
        suggestedRegion = Region.CANADA, // Suggests Global
        // Using the global/Canadian icon as per PROJECT_CONTEXT.md
        flagIconPath = Res.Images.Home.Stats.ICON_INDIA,
        messageText = "You are currently visiting our Indian site. To view our global website...",
        ctaText = "Click here",
        ctaUrl = "https://iregained.rehab"
    )

    Region.US -> RegionSwitcherBannerData(
        suggestedRegion = Region.CANADA, // Suggests Global
        // Using the global/Canadian icon as per PROJECT_CONTEXT.md
        flagIconPath = Res.Images.Home.Stats.ICON_USA,
        messageText = "You are currently visiting our USA site. View our global website?",
        ctaText = "Click here",
        ctaUrl = "https://iregained.rehab"
    )

    Region.CANADA -> {
        // This case will not be hit when the banner is rendered due to PageLayout logic,
        // but required for exhaustiveness. Return dummy data.
        RegionSwitcherBannerData(
            suggestedRegion = Region.CANADA,
            flagIconPath = Res.Images.Home.Stats.ICON_CANADA,
            messageText = "Dummy message",
            ctaText = "Dummy CTA",
            ctaUrl = "/"
        )
    }
}

@Composable
fun RegionSwitcherBanner() {
    var isDismissed by remember {
        mutableStateOf(localStorage.getItem(BANNER_DISMISSED_KEY) == "true")
    }

    if (isDismissed) return

    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val data = defaultRegionBannerData // Use current region as suggestion

    Box(
        modifier = RegionSwitcherBannerStyle.toModifier().fillMaxWidth().position(Position.Relative),
        contentAlignment = Alignment.Center
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().maxWidth(1200.px).padding(leftRight = 5.percent, topBottom = 15.px),
            horizontalArrangement = if (isDesktop) Arrangement.SpaceBetween else Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Suggestion Message
            Row(
                modifier = Modifier.gap(12.px), verticalAlignment = Alignment.CenterVertically
            ) {
                Image(data.flagIconPath, modifier = Modifier.size(24.px))
                SpanText(
                    data.messageText, modifier = Modifier.fontSize(16.px).fontWeight(FontWeight.Medium)
                )
            }

            // CTA and Dismiss
            Row(
                modifier = Modifier.gap(20.px), verticalAlignment = Alignment.CenterVertically
            ) {
                Link(
                    path = data.ctaUrl, modifier = RegionSwitcherButtonStyle.toModifier()
                ) {
                    SpanText(data.ctaText)
                }

                if (isDesktop) {
                    DismissButton { isDismissed = true }
                }
            }
        }

        if (!isDesktop) {
            Box(
                modifier = Modifier.align(Alignment.TopEnd).padding(all = 10.px)
            ) {
                DismissButton { isDismissed = true }
            }
        }
    }
}

@Composable
private fun DismissButton(onClick: () -> Unit) {
    SpanText(
        text = "✕", // Close icon
        modifier = RegionSwitcherCloseIconStyle.toModifier().onClick {
                localStorage.setItem(BANNER_DISMISSED_KEY, "true")
                onClick()
            })
}